import { Button } from '@/components/ui/button';
import { ExternalLink, X } from 'lucide-react';
import {
	Dispatch,
	forwardRef,
	MutableRefObject,
	SetStateAction,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import type { ChatWindowSourceType, SourcesType } from '../chat-window';
import { ConfigContext } from '@/config/config-context';

type ChatWindowSourcesProps = {
	chatWindowSources: ChatWindowSourceType;
	setChatWindowSources: Dispatch<SetStateAction<ChatWindowSourceType>>;
	sources: SourcesType[];
	chatWindowExpanded: boolean;
	footerRef?: MutableRefObject<HTMLDivElement | null>;
};

const ChatWindowSources = forwardRef<HTMLDivElement, ChatWindowSourcesProps>(
	(
		{
			chatWindowSources,
			setChatWindowSources,
			sources,
			chatWindowExpanded,
			footerRef,
		},
		ref
	) => {
		const config = useContext(ConfigContext);

		const headerHeight = 72;
		const [footerHeight, setFooterHeight] = useState(
			footerRef?.current?.scrollHeight ?? 0
		);

		useLayoutEffect(() => {
			const updateFooterHeight = () => {
				const rect = footerRef?.current?.getBoundingClientRect();
				setFooterHeight(rect ? rect.height : 0);
			};

			updateFooterHeight();

			window.addEventListener('resize', updateFooterHeight);
			return () => {
				window.removeEventListener('resize', updateFooterHeight);
			};
		}, [footerRef, footerRef?.current?.scrollHeight, chatWindowExpanded]);

		const { height, bottom } = useMemo(() => {
			const calculatedHeight = `calc(100% - ${footerHeight + headerHeight}px)`;
			const calculatedBottom = `${footerHeight}px`;
			return { height: calculatedHeight, bottom: calculatedBottom };
		}, [footerHeight]);

		return (
			<div
				ref={ref}
				style={{ height, bottom }}
				className='wlcb-absolute wlcb-z-20 wlcb-left-0 wlcb-w-full wlcb-bg-black/10 wlcb-flex wlcb-items-end wlcb-justify-center'
			>
				<div className='wlcb-w-full wlcb-h-3/4 wlcb-bg-white wlcb-rounded-t-sm wlcb-pt-6 wlcb-pb-5 wlcb-px-space-100 '>
					<div className='wlcb-flex wlcb-items-center wlcb-justify-between wlcb-mb-space-150'>
						<h2
							className={`wlcb-text-heading-2 ${config?.theme?.secondary?.textColor ? 'wlcb-text-[var(--theme-brand-secondary-text-color)]' : 'wlcb-text-text-primary'}`}
						>
							Sources
						</h2>
						<Button
							size='sm'
							variant='ghost'
							aria-label='Refresh'
							className='hover:!wlcb-bg-bubble'
							aria-description='Skickar frågan mot Chat bot och återskapar svaret.'
							aria-controls='chat-window'
							aria-expanded={chatWindowSources.open}
							icon={{
								iconType: (
									<X
										aria-hidden='true'
										className={`wlcb-size-6  ${config?.theme?.primary?.color ? 'wlcb-stroke-[var(--theme-brand-primary)]' : 'wlcb-stroke-black'}`}
									/>
								),
								orientation: 'default',
							}}
							onClick={() =>
								setChatWindowSources({
									open: false,
									index: undefined,
								})
							}
						/>
					</div>
					<ul className='wlcb-space-y-2 wlcb-h-[90%] wlcb-py-1 wlcb-overflow-auto scrollbar-thin'>
						{sources.map((source, index) => (
							<li
								key={index}
								className='wlcb-rounded-md wlcb-w-full wlcb-h-[60px] wlcb-border wlcb-border-stroke-subtle wlcb-bg-bubble-secondary hover:wlcb-bg-bubble wlcb-transition-[background]'
							>
								<a
									href={source.url}
									target='_blank'
									rel='noopener noreferrer nofollow'
									className='wlcb-w-full wlcb-h-full wlcb-rounded-md wlcb-p-space-050 wlcb-flex wlcb-flex-col wlcb-items-start wlcb-justify-center wlcb-gap-y-space-050'
								>
									<h3
										className={`wlcb-text-button ${config?.theme?.secondary?.textColor ? 'wlcb-text-theme-brand-secondary-text-color' : 'wlcb-text-text-primary'}`}
									>
										{source.title !== '' ? source.title : 'Länkad sida'}
									</h3>
									<div className='wlcb-flex wlcb-items-center wlcb-gap-x-space-050'>
										<ExternalLink
											aria-hidden='true'
											className={`wlcb-size-4 ${config?.theme?.primary?.color ? 'wlcb-stroke-[var(--theme-brand-primary)]' : 'wlcb-stroke-black'}`}
										/>
										<small
											className={`wlcb-text-caption ${config?.theme?.secondary?.textColor ? 'wlcb-text-theme-brand-secondary-text-color' : 'wlcb-text-text-primary'}`}
										>
											Link
										</small>
									</div>
									<span className='wlcb-sr-only'>Link to {source.url}</span>
								</a>
							</li>
						))}
					</ul>
				</div>
			</div>
		);
	}
);

ChatWindowSources.displayName = 'ChatWindowSources';
export default ChatWindowSources;
